// src/components/CreditView.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Card,
    CardContent,
    Divider,
    Chip,
    Alert,
    Snackbar,
    Paper,
    MenuItem,
    Select,
    InputLabel,
    FormControl
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import { getUserCredits, addCredit, subtractCredit } from '../api/creditApi';
import { verifyGiftCard, redeemGiftCard, getUserRedeemedGiftCards, getAffiliatesForGiftCard } from '../api/giftCardApi';
import { getAffiliateById } from '../api/affiliateApi';
import { formatCurrency } from '../utils/currency';

const CreditView = ({ user, affiliateId }) => {
    // Get role first
    const role = localStorage.getItem('role');

    // Algväärtused on tühjad massiivid
    const [credits, setCredits] = useState([]);

    // Juba olemasolev AddCredit plokk
    const [openRow, setOpenRow] = useState(null);
    const [operationType, setOperationType] = useState(null); // 'add' või 'subtract'
    const [creditInputs, setCreditInputs] = useState({});

    // Gift card states
    const [giftCardCode, setGiftCardCode] = useState('');
    const [selectedAffiliateId, setSelectedAffiliateId] = useState(affiliateId || '');
    const [verifiedGiftCard, setVerifiedGiftCard] = useState(null);
    const [redeemedGiftCards, setRedeemedGiftCards] = useState([]);
    const [showRedeemed, setShowRedeemed] = useState(false);
    const [affiliates, setAffiliates] = useState([]);
    const [paymentCurrency, setPaymentCurrency] = useState('EUR');

    // Snackbar state'id
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    const amountInputRegex = /^\d*([.,]\d{0,2})?$/;
    const getCreditCurrency = (credit) => credit?.affiliate?.paymentCurrency || paymentCurrency;
    const getSelectedAffiliateCurrency = () => (
        affiliates.find((affiliate) => Number(affiliate.id) === Number(selectedAffiliateId))?.paymentCurrency || paymentCurrency
    );
    const formatMoney = (value, currency = paymentCurrency) => formatCurrency(value, currency || paymentCurrency);

    // Laadime kasutaja krediidi andmed
    useEffect(() => {
        const fetchCredits = async () => {
            try {
                const data = await getUserCredits(affiliateId, user.id);
                setCredits(Array.isArray(data) ? data : []);
            } catch (error) {
                console.error("Error fetching credits", error);
                setCredits([]);
            }
        };
        fetchCredits();
    }, [affiliateId, user.id]);

    useEffect(() => {
        const fetchAffiliateCurrency = async () => {
            if (!affiliateId) return;
            try {
                const affiliate = await getAffiliateById(affiliateId);
                setPaymentCurrency(affiliate?.paymentCurrency || 'EUR');
            } catch (error) {
                setPaymentCurrency('EUR');
            }
        };

        fetchAffiliateCurrency();
    }, [affiliateId]);

    // Load redeemed gift cards for regular users
    useEffect(() => {
        if (role === 'regular') {
            fetchRedeemedGiftCards();
        }
    }, [role]);

    // Load all affiliates for gift card dropdown
    useEffect(() => {
        if (role === 'regular') {
            fetchAffiliates();
        }
    }, [role]);

    const fetchRedeemedGiftCards = async () => {
        try {
            const data = await getUserRedeemedGiftCards();
            setRedeemedGiftCards(data);
        } catch (error) {
            console.error("Error fetching redeemed gift cards", error);
        }
    };

    const fetchAffiliates = async () => {
        try {
            const data = await getAffiliatesForGiftCard();
            setAffiliates(data);
        } catch (error) {
            console.error("Error fetching affiliates", error);
        }
    };

    const handleToggleOperation = (creditId, operation) => {
        if (openRow === creditId && operationType === operation) {
            setOpenRow(null);
            setOperationType(null);
        } else {
            setOpenRow(creditId);
            setOperationType(operation);
            setCreditInputs((prev) => ({
                ...prev,
                [creditId]: { amount: '', description: '' }
            }));
        }
    };

    const handleInputChange = (creditId, field, value) => {
        if (field === 'amount') {
            const normalizedValue = value.replace(',', '.');
            if (normalizedValue !== '' && !amountInputRegex.test(normalizedValue)) {
                return;
            }

            setCreditInputs((prev) => ({
                ...prev,
                [creditId]: {
                    ...prev[creditId],
                    [field]: normalizedValue
                }
            }));
            return;
        }

        setCreditInputs((prev) => ({
            ...prev,
            [creditId]: {
                ...prev[creditId],
                [field]: value
            }
        }));
    };

    // Check if inputs for a specific credit ID are valid
    const areInputsValid = (creditId) => {
        const inputs = creditInputs[creditId];
        if (!inputs) return false;

        const amount = inputs.amount?.trim();
        const description = inputs.description?.trim();

        // Both fields must be filled and amount must be a valid number with max 2 decimals
        return amount && description && !isNaN(parseFloat(amount)) && amountInputRegex.test(amount);
    };

    const handleAddCredit = async (creditId, affiliateId) => {
        // Additional validation before proceeding
        if (!areInputsValid(creditId)) {
            return;
        }

        const inputs = creditInputs[creditId];
        const amount = parseFloat(inputs.amount);
        const description = inputs.description;

        try {
            await addCredit(user.id, affiliateId, amount, description);
            // Värskendame andmeid pärast edukat API vastust
            const updatedCredits = await getUserCredits(affiliateId, user.id);
            setCredits(Array.isArray(updatedCredits) ? updatedCredits : []);
            setOpenRow(null);
            setOperationType(null);
            setSnackbar({
                open: true,
                message: `Credit added successfully: ${formatMoney(amount, paymentCurrency)}`,
                severity: 'success'
            });
        } catch (error) {
            console.error("Error adding credit", error);
            setSnackbar({
                open: true,
                message: 'Error adding credit',
                severity: 'error'
            });
        }
    };

    const handleSubtractCredit = async (creditId, affiliateId) => {
        // Additional validation before proceeding
        if (!areInputsValid(creditId)) {
            return;
        }

        const inputs = creditInputs[creditId];
        const amount = parseFloat(inputs.amount);
        const description = inputs.description;

        // Leiame vastava krediidi, et kontrollida balansi
        const currentCredit = credits.find(credit => credit.id === creditId);
        if (!currentCredit) {
            setSnackbar({
                open: true,
                message: 'Credit account not found',
                severity: 'error'
            });
            return;
        }

        if (currentCredit.credit < amount) {
            const currentCurrency = getCreditCurrency(currentCredit);
            setSnackbar({
                open: true,
                message: `Insufficient balance. Current: ${formatMoney(currentCredit.credit, currentCurrency)}, Requested: ${formatMoney(amount, currentCurrency)}`,
                severity: 'error'
            });
            return;
        }

        try {
            await subtractCredit(user.id, affiliateId, amount, description);
            // Värskendame andmeid pärast edukat API vastust
            const updatedCredits = await getUserCredits(affiliateId, user.id);
            setCredits(Array.isArray(updatedCredits) ? updatedCredits : []);
            setOpenRow(null);
            setOperationType(null);
            setSnackbar({
                open: true,
                message: `Credit subtracted successfully: ${formatMoney(amount, paymentCurrency)}`,
                severity: 'success'
            });
        } catch (error) {
            console.error("Error subtracting credit", error);
            const errorMessage = error.response?.data?.error || 'Error subtracting credit';
            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error'
            });
        }
    };

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    // Gift card handlers
    const handleVerifyGiftCard = async () => {
        if (!giftCardCode || !selectedAffiliateId) {
            setSnackbar({
                open: true,
                message: 'Please select a club and enter gift card code',
                severity: 'error'
            });
            return;
        }

        try {
            const card = await verifyGiftCard(giftCardCode.toUpperCase(), selectedAffiliateId);
            setVerifiedGiftCard(card);
            setSnackbar({
                open: true,
                message: 'Gift card found!',
                severity: 'success'
            });
        } catch (error) {
            setVerifiedGiftCard(null);
            setSnackbar({
                open: true,
                message: error.response?.data?.error || 'Gift card not found',
                severity: 'error'
            });
        }
    };

    const handleRedeemGiftCard = async () => {
        if (!verifiedGiftCard) return;

        try {
            await redeemGiftCard(giftCardCode.toUpperCase(), selectedAffiliateId);
            
            // Refresh credits
            const updatedCredits = await getUserCredits(affiliateId, user.id);
            setCredits(Array.isArray(updatedCredits) ? updatedCredits : []);
            
            // Refresh redeemed gift cards
            await fetchRedeemedGiftCards();
            
            // Reset form
            setGiftCardCode('');
            setVerifiedGiftCard(null);
            setSelectedAffiliateId(affiliateId || '');
            
            setSnackbar({
                open: true,
                message: 'Gift card redeemed successfully!',
                severity: 'success'
            });
        } catch (error) {
            setSnackbar({
                open: true,
                message: error.response?.data?.error || 'Failed to redeem gift card',
                severity: 'error'
            });
        }
    };

    // Lisamise funktsiooni võib kasutada ainult, kui kasutaja roll on affiliate või trainer
    const canAddCredit = role === 'affiliate';

    return (
        <Box sx={{ maxWidth: 800, margin: '0 auto', px: { xs: 0, sm: 2 } }}>
            <Typography variant="h5" gutterBottom sx={{ml: { xs: 0, sm: 1 }, fontWeight: 'bold'}}>
                Credit
            </Typography>

            {/* Kui pole üldse credits, näita "Add Credit" vormi */}
            {credits.length === 0 && canAddCredit && (
                <Paper
                    elevation={3}
                    sx={{
                        mb: 4,
                        overflow: 'visible',
                        borderRadius: 2,
                        background: 'linear-gradient(to bottom, #f5f5f5, #ffffff)'
                    }}
                >
                    <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2, fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                            <AddCircleIcon sx={{ mr: 1, color: 'primary.main', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                            Add Credit
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 2 }, alignItems: { xs: 'stretch', sm: 'center' } }}>
                            <TextField
                                label="Amount"
                                type="number"
                                value={creditInputs['new']?.amount || ''}
                                onChange={(e) => handleInputChange('new', 'amount', e.target.value)}
                                InputProps={{
                                    startAdornment: <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>{paymentCurrency}</Box>,
                                }}
                                size="small"
                                inputProps={{ step: '0.01', min: '0' }}
                                sx={{ flexGrow: { xs: 1, sm: 0 }, minWidth: { sm: '120px' } }}
                            />
                            <TextField
                                label="Description"
                                value={creditInputs['new']?.description || ''}
                                onChange={(e) => handleInputChange('new', 'description', e.target.value)}
                                size="small"
                                sx={{ flexGrow: 1 }}
                            />
                            <Button
                                variant="contained"
                                onClick={() => handleAddCredit('new', affiliateId)}
                                startIcon={<AddCircleIcon />}
                                disabled={!areInputsValid('new')}
                                size="small"
                                fullWidth={{ xs: true, sm: false }}
                                sx={{
                                    opacity: areInputsValid('new') ? 1 : 0.7,
                                    cursor: areInputsValid('new') ? 'pointer' : 'not-allowed',
                                    minWidth: { sm: '100px' },
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    borderRadius: 20,
                                    '&:hover': {
                                        transform: areInputsValid('new') ? 'translateY(-2px)' : 'none',
                                        boxShadow: areInputsValid('new') ? '0 4px 8px rgba(0,0,0,0.1)' : 'none'
                                    },
                                    transition: 'all 0.2s'
                                }}
                            >
                                Apply
                            </Button>
                        </Box>
                    </CardContent>
                </Paper>
            )}

            {/* Credits-tabel */}
            <Paper
                elevation={3}
                sx={{
                    mb: 4,
                    overflow: 'hidden',
                    borderRadius: 2,
                    background: 'linear-gradient(to bottom, #f5f5f5, #ffffff)'
                }}
            >
                <CardContent sx={{ pb: 0, px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2, fontSize: { xs: '1.1rem', sm: '1.25rem' }, fontWeight: 'medium' }}>
                        <AccountBalanceWalletIcon sx={{ mr: 1, color: 'primary.main', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                        Available Credit
                    </Typography>
                    <Divider sx={{ mb: 1 }} />
                </CardContent>

                <Box sx={{ overflowX: 'auto', px: { xs: 0, sm: 0 } }}>
                    <Table sx={{ minWidth: { xs: 300, sm: 600 } }}>
                        <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableRow>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    padding: { xs: '8px 12px', sm: '16px' },
                                    minWidth: { xs: '80px', sm: 'auto' }
                                }}>
                                    Amount
                                </TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                    padding: { xs: '8px 12px', sm: '16px' },
                                    minWidth: { xs: '100px', sm: 'auto' }
                                }}>
                                    Affiliate
                                </TableCell>
                                {canAddCredit && (
                                    <TableCell sx={{
                                        fontWeight: 'bold',
                                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                        padding: { xs: '8px 12px', sm: '16px' },
                                        minWidth: { xs: '120px', sm: 'auto' },
                                        textAlign: 'center'
                                    }}>
                                        Actions
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {credits.map((credit) => (
                                <React.Fragment key={credit.id}>
                                    <TableRow>
                                        <TableCell sx={{
                                            padding: { xs: '8px 12px', sm: '16px' },
                                            minWidth: { xs: '80px', sm: 'auto' }
                                        }}>
                                            <Typography sx={{
                                                fontWeight: 'bold',
                                                color: '#2ecc71',
                                                fontSize: { xs: '0.9rem', sm: '1rem' }
                                            }}>
                                                {formatMoney(credit.credit, getCreditCurrency(credit))}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{
                                            padding: { xs: '8px 12px', sm: '16px' },
                                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                            minWidth: { xs: '100px', sm: 'auto' }
                                        }}>
                                            {credit.affiliate?.name}
                                        </TableCell>
                                        {canAddCredit && (
                                            <TableCell sx={{
                                                padding: { xs: '8px 12px', sm: '16px' },
                                                minWidth: { xs: '120px', sm: 'auto' },
                                                textAlign: 'center'
                                            }}>
                                                <Box sx={{
                                                    display: 'flex',
                                                    flexDirection: { xs: 'column', sm: 'row' },
                                                    gap: { xs: 0.5, sm: 1 },
                                                    justifyContent: 'center'
                                                }}>
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<AddCircleIcon sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }} />}
                                                        onClick={() => handleToggleOperation(credit.id, 'add')}
                                                        color="primary"
                                                        size="small"
                                                        sx={{
                                                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                            minWidth: { xs: '50px', sm: '70px' },
                                                            height: { xs: '28px', sm: '32px' },
                                                            padding: { xs: '4px 8px', sm: '4px 12px' },
                                                            borderRadius: 20,
                                                            '&:hover': {
                                                                transform: 'translateY(-2px)',
                                                                boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                                                            },
                                                            transition: 'all 0.2s'
                                                        }}
                                                    >
                                                        Add
                                                    </Button>
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={<RemoveCircleIcon sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }} />}
                                                        onClick={() => handleToggleOperation(credit.id, 'subtract')}
                                                        color="error"
                                                        size="small"
                                                        sx={{
                                                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                                            minWidth: { xs: '50px', sm: '70px' },
                                                            height: { xs: '28px', sm: '32px' },
                                                            padding: { xs: '4px 8px', sm: '4px 12px' },
                                                            borderRadius: 20,
                                                            '&:hover': {
                                                                transform: 'translateY(-2px)',
                                                                boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                                                            },
                                                            transition: 'all 0.2s'
                                                        }}
                                                    >
                                                        Sub
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                    {openRow === credit.id && (
                                        <TableRow>
                                            <TableCell colSpan={canAddCredit ? 3 : 2} sx={{ padding: { xs: '12px', sm: '16px' } }}>
                                                <Paper
                                                    elevation={2}
                                                    sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 2,
                                                        p: { xs: 2, sm: 3 },
                                                        backgroundColor: operationType === 'add' ? '#e8f5e8' : '#ffeaea',
                                                        border: operationType === 'add' ? '1px solid #4caf50' : '1px solid #f44336',
                                                        borderRadius: 2,
                                                        '&:hover': {
                                                            transform: 'translateY(-2px)',
                                                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                                        },
                                                        transition: 'all 0.2s'
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 2 } }}>
                                                        <TextField
                                                            label="Amount"
                                                            type="number"
                                                            value={creditInputs[credit.id]?.amount || ''}
                                                            onChange={(e) => handleInputChange(credit.id, 'amount', e.target.value)}
                                                            InputProps={{
                                                                startAdornment: <Box component="span" sx={{ color: 'text.secondary', mr: 1 }}>{getCreditCurrency(credit)}</Box>,
                                                            }}
                                                            size="small"
                                                            inputProps={{ step: '0.01', min: '0' }}
                                                            sx={{
                                                                flexGrow: { xs: 1, sm: 0 },
                                                                minWidth: { sm: '120px' }
                                                            }}
                                                        />
                                                        <TextField
                                                            label="Description"
                                                            value={creditInputs[credit.id]?.description || ''}
                                                            onChange={(e) => handleInputChange(credit.id, 'description', e.target.value)}
                                                            size="small"
                                                            sx={{ flexGrow: 1 }}
                                                        />
                                                    </Box>
                                                    <Button
                                                        variant="contained"
                                                        onClick={() => {
                                                            if (operationType === 'add') {
                                                                handleAddCredit(credit.id, credit.affiliateId);
                                                            } else {
                                                                handleSubtractCredit(credit.id, credit.affiliateId);
                                                            }
                                                        }}
                                                        startIcon={operationType === 'add' ? <AddCircleIcon /> : <RemoveCircleIcon />}
                                                        disabled={!areInputsValid(credit.id)}
                                                        color={operationType === 'add' ? 'primary' : 'error'}
                                                        size="small"
                                                        sx={{
                                                            opacity: areInputsValid(credit.id) ? 1 : 0.7,
                                                            cursor: areInputsValid(credit.id) ? 'pointer' : 'not-allowed',
                                                            alignSelf: { xs: 'stretch', sm: 'flex-start' },
                                                            maxWidth: { sm: '150px' },
                                                            borderRadius: 20,
                                                            '&:hover': {
                                                                transform: areInputsValid(credit.id) ? 'translateY(-2px)' : 'none',
                                                                boxShadow: areInputsValid(credit.id) ? '0 4px 8px rgba(0,0,0,0.1)' : 'none'
                                                            },
                                                            transition: 'all 0.2s'
                                                        }}
                                                    >
                                                        {operationType === 'add' ? 'Add' : 'Subtract'}
                                                    </Button>
                                                </Paper>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            </Paper>

            {/* Gift Card Redemption Section - Only for regular users */}
            {role === 'regular' && (
                <>
                    <Paper
                        elevation={3}
                        sx={{
                            mb: 4,
                            overflow: 'hidden',
                            borderRadius: 2,
                            background: 'linear-gradient(to bottom, #f5f5f5, #ffffff)'
                        }}
                    >
                        <CardContent sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
                            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2, fontSize: { xs: '1.1rem', sm: '1.25rem' }, fontWeight: 'medium' }}>
                                <CardGiftcardIcon sx={{ mr: 1, color: 'primary.main', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                                Redeem Gift Card
                            </Typography>
                            <Divider sx={{ mb: 2 }} />

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                                    <TextField
                                        select
                                  
                                        value={selectedAffiliateId}
                                        onChange={(e) => {
                                            setSelectedAffiliateId(e.target.value);
                                            setVerifiedGiftCard(null);
                                        }}
                                        size="small"
                                        sx={{ flexGrow: 1 }}
                                        SelectProps={{
                                            native: true,
                                        }}
                                    >
                                        <option value="">Select club</option>
                                        {affiliates.map((affiliate) => (
                                            <option key={affiliate.id} value={affiliate.id}>
                                                {affiliate.name}
                                            </option>
                                        ))}
                                    </TextField>

                                    <TextField
                                        label="Gift Card Code"
                                        value={giftCardCode}
                                        onChange={(e) => {
                                            setGiftCardCode(e.target.value.toUpperCase());
                                            setVerifiedGiftCard(null);
                                        }}
                                        placeholder="XXXX-XXXX-XXXX"
                                        size="small"
                                        sx={{ flexGrow: 1 }}
                                        inputProps={{ style: { fontFamily: 'monospace', textTransform: 'uppercase' } }}
                                    />

                                    <Button
                                        variant="outlined"
                                        onClick={handleVerifyGiftCard}
                                        disabled={!giftCardCode || !selectedAffiliateId}
                                        size="small"
                                        sx={{ minWidth: { sm: '120px' }, borderRadius: 20 }}
                                    >
                                        Verify
                                    </Button>
                                </Box>

                                {verifiedGiftCard && (
                                    <Paper
                                        elevation={2}
                                        sx={{
                                            p: 2,
                                            backgroundColor: '#e8f5e8',
                                            border: '1px solid #4caf50',
                                            borderRadius: 2
                                        }}
                                    >
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                            Gift Card Details:
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            <Typography variant="body2">
                                                <strong>Name:</strong> {verifiedGiftCard.name}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Amount:</strong> {formatMoney(verifiedGiftCard.amount, verifiedGiftCard.affiliate?.paymentCurrency || getSelectedAffiliateCurrency())}
                                            </Typography>
                                            <Typography variant="body2">
                                                <strong>Valid Until:</strong> {new Date(verifiedGiftCard.validUntil).toLocaleDateString('en-US')}
                                            </Typography>
                                        </Box>
                                        <Button
                                            variant="contained"
                                            color="success"
                                            onClick={handleRedeemGiftCard}
                                            sx={{ mt: 2, borderRadius: 20 }}
                                            fullWidth
                                        >
                                            Redeem Gift Card
                                        </Button>
                                    </Paper>
                                )}
                            </Box>
                        </CardContent>
                    </Paper>

                    {/* Redeemed Gift Cards */}
                    {redeemedGiftCards.length > 0 && (
                        <Paper
                            elevation={3}
                            sx={{
                                mb: 4,
                                overflow: 'hidden',
                                borderRadius: 2,
                                background: 'linear-gradient(to bottom, #f5f5f5, #ffffff)'
                            }}
                        >
                            <CardContent sx={{ pb: 0, px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>
                                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2, fontSize: { xs: '1.1rem', sm: '1.25rem' }, fontWeight: 'medium' }}>
                                    <CardGiftcardIcon sx={{ mr: 1, color: 'primary.main', fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
                                    Redeemed Gift Cards
                                </Typography>
                                <Divider sx={{ mb: 1 }} />
                            </CardContent>

                            <Box sx={{ overflowX: 'auto' }}>
                                <Table>
                                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                Name
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                Amount
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                Club
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                                Redeemed
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {redeemedGiftCards.map((card) => (
                                            <TableRow key={card.id}>
                                                <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                                    {card.name}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography sx={{ fontWeight: 'bold', color: '#2ecc71', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                                        {formatMoney(card.amount, card.affiliate?.paymentCurrency || paymentCurrency)}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                                    {card.affiliate?.name}
                                                </TableCell>
                                                <TableCell sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                                    {new Date(card.redeemedAt).toLocaleDateString('et-EE')}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Paper>
                    )}
                </>
            )}

            {/* Snackbar for feedback */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default CreditView;